import React from "react";
import { Chip, Grid, Typography } from "@mui/material";

import { style } from "./style";

import Button from "@/components/Button";
import Modal from "@/components/Modal";
import BlockedAvailability from "../BlockedAvailability";
import GetImages from "@/components/GetImages";

import { IButtonVariant } from "@/models/button";
import {
  IDoctorConsultationSelection,
  IDoctorConsultationType,
  IProfileDropdownEnums,
} from "@/models/doctor";

import useIsTablet from "@/utils/hooks/useIsTablet";
import useIsMobile from "@/utils/hooks/useIsMobile";
import {
  capitalizeFirstLetter,
  getDateFormat,
  timeInAmPmFormat,
  topBarDropdownDispatchingEnums,
} from "@/utils";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  availabilitySettings,
  fetchAvailabilitySettingsListing,
  setIsBlockedBookingShown,
  setIsEditAvailability,
} from "@/store/slices/doctor/availabilitySettings";
import { setTopBarDropdownMultipleItems } from "@/store/slices/doctor/topNavigation";

interface IDetailedAvailabilityProps {
  isOpen?: boolean;
  closeHandler?: () => void;
}
const DetailedAvailability: React.FC<IDetailedAvailabilityProps> = ({
  isOpen,
  closeHandler,
}) => {
  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const isMobileOrTablet = isMobile || isTablet;

  const dispatch = useAppDispatch();
  const { isBlockedBookingShown, selectedAvailabilitySettingDetail } =
    useAppSelector(availabilitySettings);

  const {
    type_of_consultation,
    schedule_date,
    clinic_name,
    total_slots,
    booked,
    start_and_end_time,
    blocked_roles,
  } = selectedAvailabilitySettingDetail || {};

  const blockedBookingOpenCloseHandler = () =>
    dispatch(setIsBlockedBookingShown(!isBlockedBookingShown));

  const editAvailabilityHandler = () => {
    dispatch(setIsEditAvailability(true));
    dispatch(
      setTopBarDropdownMultipleItems([
        topBarDropdownDispatchingEnums[
          IProfileDropdownEnums.AVAILABILITY_SETTINGS
        ],
        topBarDropdownDispatchingEnums[
          IProfileDropdownEnums.CREATE_EDIT_AVAILABILITY
        ],
      ])
    );
  };

  const saveBlockedBookingHandler = () => {
    blockedBookingOpenCloseHandler();
    dispatch(fetchAvailabilitySettingsListing({}));
  };

  const detailedAvailabilityScreen = () => (
    <Grid item sx={style.container}>
      <Typography component={"h5"} sx={style.heading}>
        {getDateFormat(schedule_date, "DD MMM, YYYY")}
      </Typography>
      <Grid item>
        <Typography component={"p"} sx={style.clinicNameOrPlatform}>
          {type_of_consultation === IDoctorConsultationType.IN_CLINIC
            ? clinic_name
            : IDoctorConsultationSelection.VIDEO_CONSULTATION}
        </Typography>
      </Grid>
      <Grid item sx={style.totalBookedContainer}>
        <Grid item sx={style.slotContainer}>
          <Typography component={"p"}>Slot {total_slots}</Typography>
        </Grid>
        <Typography component={"p"} sx={style.totalBooked}>
          {booked} Booked
        </Typography>
      </Grid>
      <Grid item sx={style.appointmentTimeRangeContainer}>
        <Typography component={"p"}>
          {start_and_end_time
            ?.map(
              (time) =>
                timeInAmPmFormat(time.start_time) +
                " - " +
                timeInAmPmFormat(time.end_time)
            )
            ?.join(", ")}
        </Typography>
      </Grid>
      <Grid item sx={style.blockedBookingHeadingContainer}>
        <Typography component={"h5"}>Blocked Booking for</Typography>
        <Grid item sx={style.blockBookingForChipContainer}>
          {blocked_roles?.length > 0 ? (
            blocked_roles?.map((blockedRole) => (
              <Chip
                sx={style.blockBookingForChip}
                label={capitalizeFirstLetter(blockedRole)}
              />
            ))
          ) : (
            <Typography component={"p"}>None</Typography>
          )}
        </Grid>
      </Grid>

      <Grid item sx={style.blockBookingContainer}>
        <BlockedAvailability />
      </Grid>
      <Grid item sx={style.editAvailabilityButtonContainer}>
        <Button
          variant={IButtonVariant.WHITE}
          btnTrigger={editAvailabilityHandler}
        >
          Edit Availability
        </Button>
        <Grid item sx={style.blockBookingButtonContainer}>
          <Button
            variant={IButtonVariant.WHITE}
            btnTrigger={blockedBookingOpenCloseHandler}
          >
            Block Booking
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
  return (
    <>
      {isMobileOrTablet ? (
        <Modal
          isOpen={isOpen}
          closeHandler={closeHandler}
          isCloseIcon={true}
          backgroundColor="var(--neutral-100)"
          lineBottomBackground="var(--neutral-100)"
          children={detailedAvailabilityScreen()}
        />
      ) : (
        detailedAvailabilityScreen()
      )}

      {isBlockedBookingShown && (
        <Modal
          isOpen={isBlockedBookingShown}
          closeHandler={blockedBookingOpenCloseHandler}
        >
          <Grid item sx={style.blockedBookingContainer}>
            <Grid
              item
              sx={style.blockedBookingHeadingBoxContainer}
              onClick={blockedBookingOpenCloseHandler}
            >
              <GetImages name="LeftArrowIcon" width={"26"} height={"16"} />
              <Typography component={"h5"} sx={style.blockedBookingHeading}>
                Block Booking
              </Typography>
            </Grid>
            <Grid item sx={style.blockedBookingHeadingInnerContainer}>
              <Typography
                component={"p"}
                sx={style.blockedBookingHeadingInnerText}
              >
                {selectedAvailabilitySettingDetail?.clinic_name}
              </Typography>
              <Typography
                component={"p"}
                sx={style.blockedBookingHeadingDateText}
              >
                {getDateFormat(
                  selectedAvailabilitySettingDetail?.schedule_date,
                  "DD MMM, YYYY"
                )}
              </Typography>
            </Grid>
            <BlockedAvailability />
            <Button btnTrigger={saveBlockedBookingHandler}>Save</Button>
          </Grid>
        </Modal>
      )}
    </>
  );
};

export default DetailedAvailability;
